/* eslint-disable prettier/prettier */
import { apiService } from './api.service';

/**
 * ================================
 * Types
 * ================================
 */

export interface CourseSubmissionFilters {
	assessmentId?: string;
	learnerId?: string;
	status?: 'graded' | 'pending';
	page?: number;
	limit?: number;
}

export interface CourseSubmissionFile {
	fileid: string;
	filename: string;
	filesize: number;
	mimetype: string;
	filepurpose: string;
	uploadedat: string | Date;
}

export interface CourseSubmission {
	id: string;
	assessmentid: string;
	userid: string;
	attemptnumber: number;

	score?: number;
	maxscore?: number;
	passed?: boolean;

	submittedat?: string | Date;
	reviewedat?: string | Date;

	attachmentcount: number;
	totalfilesize: number;

	assessment_title: string;
	assessmenttype: string;
	passinggrade?: number;

	student_name: string;
	student_email: string;

	reviewer_name?: string;

	files: CourseSubmissionFile[];
}

export interface CourseSubmissionsResult {
	submissions: CourseSubmission[];
	total: number;
	page: number;
	limit: number;
}

/**
 * ================================
 * Service
 * ================================
 */

class AssessmentSubmissionService {
	/**
	 * Get all submissions for a course
	 *
	 * GET:
	 * /course/:courseId/submissions
	 */
	async getCourseSubmissions(
		courseId: string,
		filters: CourseSubmissionFilters = {}
	): Promise<CourseSubmissionsResult> {
		try {
			if (!courseId) {
				throw new Error('Course ID is required.');
			}

			const params: CourseSubmissionFilters = {
				...(filters.assessmentId
					? { assessmentId: filters.assessmentId }
					: {}),

				...(filters.learnerId
					? { learnerId: filters.learnerId }
					: {}),

				...(filters.status ? { status: filters.status } : {}),

				page: filters.page ?? 1,
				limit: filters.limit ?? 20,
			};

			const response =
				await apiService.get<CourseSubmissionsResult>(
					`/submission/course/${encodeURIComponent(courseId)}/submissions`,
					params
				);

			/**
			 * Your apiService already returns:
			 *
			 * response.data
			 *
			 * which should be the actual API response:
			 *
			 * {
			 *   success: true,
			 *   data: {...}
			 * }
			 */

			if (!response?.success) {
				throw new Error(
					response?.message || 'Failed to fetch course submissions.'
				);
			}

			if (!response.data) {
				throw new Error(
					'Course submissions response did not contain any data.'
				);
			}

			return response.data;
		} catch (error: any) {
			console.error(
				'[AssessmentSubmissionService] getCourseSubmissions error:',
				error
			);

			/**
			 * Axios interceptor will already handle 401 globally.
			 * Here we simply convert the error into a useful Error
			 * for the hook/component.
			 */
			const message =
				error?.response?.data?.message ||
				error?.message ||
				'Failed to fetch course submissions.';

			throw new Error(message);
		}
	}
}

export const assessmentSubmissionService =
	new AssessmentSubmissionService();